// Status.js
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { sendStatusMessage } from "../utils/sendStatusMessage";
import { StatusInput } from "../utils/StatusInput"; 
import { uploadMedia } from "../utils/MediaUtils";
import { EmojiPicker, insertEmojiAtCursor } from "../components/emoji";
// NOTE: EmojiPicker/insertEmojiAtCursor are only ever rendered/invoked when
// `!mobileView` (see StatusComposer below) — mobile relies on the device's
// native keyboard emoji instead of this in-app picker.

/* ─────────────────────────────────────────────────────────────────────────────
   CONSTANTS
─────────────────────────────────────────────────────────────────────────────── */
const DEFAULT_DURATION_MS = 5000;

const TEXT_BACKGROUNDS = [
  "#25D366", // green
  "#128C7E", // teal
  "#075E54", // deep teal
  "#34B7F1", // sky
  "#8E44AD", // violet
  "#E67E22", // amber
  "#C0392B", // brick
];

/* Small gap (in degrees) left between each segment of the story ring */
const RING_SEGMENT_GAP_DEG = 8;

/* ─────────────────────────────────────────────────────────────────────────────
   ICONS (inline SVG — no external icon lib)
─────────────────────────────────────────────────────────────────────────────── */
function IconPlus(props) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...props}>
      <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
    </svg>
  );
}
function IconCamera(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
      <path d="M9.4 4l-1.8 2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.6L14.6 4H9.4zM12 9a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
    </svg>
  );
}
function IconText(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
      <path d="M5 4h14v2h-6v14h-2V6H5V4z" />
    </svg>
  );
}
function IconClose(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...props}>
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}
function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
      <path d="M6 7h12l-1 13H7L6 7zm3-4h6l1 2h4v2H2V5h4l1-2zM10 9v8h1V9h-1zm3 0v8h1V9h-1z" />
    </svg>
  );
}
function IconEye(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
      <path d="M12 5c-5.5 0-9.7 4.1-11 7 1.3 2.9 5.5 7 11 7s9.7-4.1 11-7c-1.3-2.9-5.5-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
    </svg>
  );
}
function IconSend(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
      <path d="M3 20l18-8L3 4v6l12 2-12 2v6z" />
    </svg>
  );
}
function IconChevronLeft(props) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...props}>
      <path d="M15.4 7.4L14 6l-6 6 6 6 1.4-1.4L10.8 12z" />
    </svg>
  );
}
function IconEmoji(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...props}>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-3.5-9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm7 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-3.5 7c-2.28 0-4.22-1.31-5.06-3.2a.75.75 0 0 1 1.37-.6C8.98 15.5 10.38 16.5 12 16.5s3.02-1 3.69-2.3a.75.75 0 1 1 1.37.6C16.22 16.69 14.28 18 12 18z" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS (pure UI formatting — no data fetching)
─────────────────────────────────────────────────────────────────────────────── */
function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFromString(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
}

/* Second, offset hue used to build a subtle two-tone gradient for story cards */
function colorFromStringAlt(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = (Math.abs(hash) % 360 + 40) % 360;
  return `hsl(${hue}, 60%, 28%)`;
}

function formatTimeAgo(value) {
  const ts = new Date(value).getTime();
  if (isNaN(ts)) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));

  if (diffSec < 60)    return "just now";
  if (diffSec < 3600)  return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

/* Builds a conic-gradient ring divided into N equal segments with small gaps
   between each — 1 status = full ring, 2 = two semicircles, 3 = three arcs, etc. */
function buildRingGradient(segments = 1, color = "#25D366") {
  const n = Math.max(1, Math.floor(segments) || 1);

  if (n === 1) {
    return `conic-gradient(${color} 0deg 355deg, transparent 355deg 360deg)`;
  }

  const segAngle = 360 / n;
  const gap = Math.min(RING_SEGMENT_GAP_DEG, segAngle * 0.3);
  const stops = [];

  for (let i = 0; i < n; i++) {
    const start = i * segAngle;
    const end = start + segAngle - gap;
    const nextStart = start + segAngle;
    stops.push(`${color} ${start}deg ${end}deg`);
    stops.push(`transparent ${end}deg ${nextStart}deg`);
  }

  return `conic-gradient(${stops.join(", ")})`;
}

/* Returns a NEW array of statuses ordered current → old (newest first).
   Used everywhere a status group is built or opened, so that:
     - story-rail preview tiles always show the latest update, and
     - the StatusViewer plays back newest → oldest (tapping "next" moves
       from the current status toward older ones).
   Falls back to 0 for missing/invalid createdAt so malformed records
   don't throw off the sort (they just sink to the end). */
function sortStatusesNewestFirst(statuses = []) {
  return [...statuses].sort((a, b) => {
    const bTime = new Date(b?.createdAt).getTime();
    const aTime = new Date(a?.createdAt).getTime();
    return (isNaN(bTime) ? 0 : bTime) - (isNaN(aTime) ? 0 : aTime);
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   AVATAR — with unviewed/viewed ring, matching WhatsApp's style.
   `segments` controls how many arcs the ring is divided into (= number of
   statuses in the group). Defaults to 1 (a single unbroken ring).
─────────────────────────────────────────────────────────────────────────────── */
function Avatar({ src, name, size = 56, ringed = false, ringComplete = false, segments = 1 }) {
  const inner = size - 6;
  const ringColor = ringComplete ? "#9CA3AF" : "#25D366";
  const ringBackground = ringed ? buildRingGradient(segments, ringColor) : "transparent";

  return (
    <div
      className="relative shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        padding: ringed ? 2.5 : 0,
        background: ringBackground,
      }}
    >
           <div
        className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-neutral-900 flex items-center justify-center"
        style={{ padding: ringed ? 2 : 0 }}
      >
        {src ? (
          <img
            src={src}
            alt={name}
            className="w-full h-full rounded-full object-cover"
            style={{ width: inner, height: inner }}
          />
        ) : (
          <div
            className="rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center"
            style={{
              width: inner,
              height: inner,
              fontSize: inner * 0.5,
            }}
          >
            👤
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   ADD-STATUS CARD — first tile in the horizontal story rail
─────────────────────────────────────────────────────────────────────────────── */
function AddStatusCard({ avatarSrc, name, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative shrink-0 w-28 h-40 sm:w-32 sm:h-44 rounded-2xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex flex-col items-center justify-center gap-2 touch-manipulation active:scale-[0.97] transition-transform"
    >
      <div className="relative">
        <Avatar src={avatarSrc} name={name} size={56} />
        <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] text-white border-2 border-white dark:border-neutral-800">
          <IconPlus />
        </span>
      </div>
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 px-1 text-center">
        Add status
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STORY CARD — a single status/status-group tile in the horizontal rail
─────────────────────────────────────────────────────────────────────────────── */
function StatusStoryCard({
  name,
  avatarSrc,
  ringComplete,
  segments = 1,
  subtitle,
  onClick,
  statusType,
  previewText,
  previewMediaUrl,
  previewThumbnail,
  previewBackgroundColor,
}) {
  /* Media (image/video) statuses show the decrypted media/thumbnail as
     the tile background instead of a plain name-derived gradient. */
  const mediaPreviewSrc =
    statusType && statusType !== "text"
      ? previewThumbnail || previewMediaUrl
      : null;

  /* Text statuses show the actual status text on top of its chosen
     background color instead of a generic name-derived gradient. */
  const showTextPreview = statusType === "text" && !!previewText;

  return (
    <button
      onClick={onClick}
      className="relative shrink-0 w-28 h-40 sm:w-32 sm:h-44 rounded-2xl overflow-hidden text-left touch-manipulation active:scale-[0.97] transition-transform"
    >
      {/* background */}
      {mediaPreviewSrc ? (
        <img
          src={mediaPreviewSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: showTextPreview
              ? previewBackgroundColor || TEXT_BACKGROUNDS[0]
              : `linear-gradient(160deg, ${colorFromString(name)}, ${colorFromStringAlt(name)})`,
          }}
        />
      )}

      {/* text-status preview content */}
      {showTextPreview && (
        <div className="absolute inset-0 flex items-center justify-center px-3">
          <p className="text-white text-xs font-medium text-center leading-snug line-clamp-5 drop-shadow">
            {previewText}
          </p>
        </div>
      )}

      {/* readability gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/70" />

      {/* avatar + ring */}
      <div className="absolute top-2 left-2">
        <Avatar
          src={avatarSrc}
          name={name}
          size={36}
          ringed
          ringComplete={ringComplete}
          segments={segments}
        />
      </div>

      {/* name / subtitle */}
      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-white text-xs font-semibold truncate drop-shadow">{name}</p>
        {subtitle && (
          <p className="text-white/80 text-[11px] truncate drop-shadow">{subtitle}</p>
        )}
      </div>
    </button>
  );
}

/* Tracks how many px the on-screen keyboard is currently covering at the
   bottom of the viewport, via the visualViewport API. On mobile, a
   `fixed inset-0` container is sized to the *layout* viewport (which does
   NOT shrink when the keyboard opens) — only the *visual* viewport shrinks.
   That's why bottom-anchored elements (send button, color swatches) end up
   hidden behind the keyboard: they're positioned correctly relative to the
   window, just not relative to what's actually visible. This hook lets us
   nudge those elements up by exactly that amount while the keyboard is open. */
function useKeyboardOffset(active) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!active) {
      setOffset(0);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) return;

    const handleViewportChange = () => {
      const inset = Math.max(
        0,
        Math.round(window.innerHeight - vv.height - vv.offsetTop)
      );
      setOffset(inset);
    };

    handleViewportChange();
    vv.addEventListener("resize", handleViewportChange);
    vv.addEventListener("scroll", handleViewportChange);

    return () => {
      vv.removeEventListener("resize", handleViewportChange);
      vv.removeEventListener("scroll", handleViewportChange);
    };
  }, [active]);

  return offset;
}

/* ─────────────────────────────────────────────────────────────────────────────
   COMPOSER — pick text or media status

   Footer behavior:
     - Text mode: full-screen textarea for the status content itself, plus a
       small footer (emoji toggle — DESKTOP ONLY — and a post/send button).
     - Media mode: caption entry now reuses the same StatusInput pill bar
       used for status replies (emoji icon, text field, attach, mic), so the
       composer looks and behaves the same way the reply bar does instead of
       a bespoke caption input. Hitting its send arrow posts the status with
       whatever caption (if any) was typed.
─────────────────────────────────────────────────────────────────────────────── */
function StatusComposer({ onClose, onSubmit, mobileView }) {
  const [mode, setMode] = useState(null); // "text" | "media"
  const [text, setText] = useState("");
  const [bgIndex, setBgIndex] = useState(0);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Keeps the send button / color swatches visible above the keyboard on
  // mobile when the text input or caption field is focused.
  const keyboardOffset = useKeyboardOffset(mobileView);

  const handlePickMedia = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMode("media");
  };

  useEffect(() => {
    return () => {
      if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    };
  }, [mediaPreview]);

  /* Inserts the picked emoji into the text-status textarea. Desktop only —
     see the footer below, where the emoji toggle is gated on !mobileView.
     Falls back to reading the DOM node's value directly in case the
     dispatched "input" event doesn't get picked up by the controlled
     input's onChange. */
  const handleEmojiSelect = (emoji) => {
    insertEmojiAtCursor(textareaRef.current, emoji);
    const el = textareaRef.current;
    setText(el ? el.value : (t) => t + emoji);
  };

  /* Posts the status. For media mode, `caption` comes from the StatusInput
     bar's send callback (may be empty — captions are optional). */
  const handleSubmit = (caption = "") => {
    if (mode === "text") {
      if (!text.trim()) return;
      onSubmit({
        statusType: "text",
        text: text.trim(),
        backgroundColor: TEXT_BACKGROUNDS[bgIndex],
      });
      return;
    }

    if (mode === "media" && mediaFile) {
      onSubmit({
        statusType: mediaFile.type.startsWith("video") ? "video" : "image",
        media: mediaFile,
        caption: caption.trim() || null,
        mediaMimeType: mediaFile.type,
        mediaSize: mediaFile.size,
      });
    }
  };

  return (
  <div
  className={[
    mobileView
      ? "fixed inset-0"
      : "absolute inset-0",
    "z-[60] bg-black flex flex-col",
  ].join(" ")}
>
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <button onClick={onClose} className="p-1 -ml-1">
          <IconClose />
        </button>
        <span className="text-sm font-medium">New status</span>
        <div className="w-6" />
      </div>

      {/* body */}
      <div className="flex-1 flex items-center justify-center px-6">
        {mode === "text" && (
          <textarea
            ref={textareaRef}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a status"
            rows={4}
            className="w-full max-w-md resize-none bg-transparent text-white text-2xl text-center placeholder-white/60 outline-none"
          />
        )}

        {mode === "media" && mediaPreview && (
          <div className="w-full max-w-md flex flex-col items-center gap-4">
            {mediaFile?.type.startsWith("video") ? (
              <video src={mediaPreview} className="max-h-[60vh] rounded-lg" controls />
            ) : (
              <img src={mediaPreview} alt="" className="max-h-[60vh] rounded-lg object-contain" />
            )}
          </div>
        )}

        {!mode && (
          <div className="flex flex-col items-center gap-4 text-white/80">
            <p className="text-sm">Share a photo, video, or text update</p>
            <div className="flex gap-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <IconCamera />
                <span className="text-xs">Photo / video</span>
              </button>
              <button
                onClick={() => setMode("text")}
                className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <IconText />
                <span className="text-xs">Text</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handlePickMedia}
      />

      {/* Bottom bar (background swatches + footer). Translated upward by
          `keyboardOffset` on mobile so it stays above the on-screen
          keyboard instead of being hidden behind it when the text input /
          caption field is focused. */}
      <div
        style={{
          transform:
            mobileView && keyboardOffset > 0
              ? `translateY(-${keyboardOffset}px)`
              : undefined,
          transition: "transform 0.15s ease-out",
        }}
      >
        {/* text background swatches */}
        {mode === "text" && (
          <div className="flex items-center justify-center gap-2 pb-4">
            {TEXT_BACKGROUNDS.map((c, i) => (
              <button
                key={c}
                onClick={() => setBgIndex(i)}
                className="w-6 h-6 rounded-full border-2"
                style={{
                  background: c,
                  borderColor: i === bgIndex ? "#fff" : "transparent",
                }}
              />
            ))}
          </div>
        )}

        {/* footer — text mode: emoji (desktop only) + post button */}
        {mode === "text" && (
          <div className="relative flex items-center px-6 pb-6">
            {!mobileView && (
              <button
                onClick={() => setShowEmoji((s) => !s)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white active:scale-95 transition-transform"
                aria-label="Insert emoji"
              >
                <IconEmoji />
              </button>
            )}

            <button
              onClick={() => handleSubmit()}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-[#25D366] text-white shadow-lg active:scale-95 transition-transform ml-auto"
              aria-label="Post status"
            >
              <IconSend />
            </button>

            {!mobileView && showEmoji && (
              <div className="absolute bottom-16 left-6 w-72 h-80 rounded-xl overflow-hidden shadow-2xl border border-white/10 z-10">
                <EmojiPicker onSelect={handleEmojiSelect} />
              </div>
            )}
          </div>
        )}

        {/* footer — media mode: same reply-style StatusInput bar for the
            caption. Hitting its send arrow posts the status; caption is
            optional (allowEmptySend keeps the send button active even when
            nothing's typed). */}
        {mode === "media" && mediaFile && (
          <div className="px-4 pb-6">
            <StatusInput
              placeholder="Add a caption…"
              autoFocus
              allowEmptySend
              onSendText={(trimmed) => handleSubmit(trimmed)}
              onSendMedia={(file) => {
                if (!file) return;
                setMediaFile(file);
                setMediaPreview(URL.createObjectURL(file));
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        textarea::-webkit-scrollbar { display: none; }
      `}</style>
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            mode === "text" ? TEXT_BACKGROUNDS[bgIndex] : "transparent",
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   VIEWER — full-screen story playback
─────────────────────────────────────────────────────────────────────────────── */
function StatusViewer({
  ownerChatId,
  ownerName,
  ownerAvatar,
  statuses,
  startIndex = 0,
  isOwn,
  onClose,
  onFinishedAll,
  onView,
  onDelete,
  onReply,
  replySending,
    mobileView,
}) {
 
  const [index, setIndex]       = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused]     = useState(false);

  const rafRef     = useRef(null);
  const startRef   = useRef(null);
  const elapsedRef = useRef(0);
  const videoRef   = useRef(null);
  const footerRef  = useRef(null); // wraps the reply bar — used to keep the
                                    // story paused for as long as focus stays
                                    // inside it (typing a reply, picking an
                                    // attachment, etc.)
  const current  = statuses[index];
  const duration =
    current?.statusType === "video"
      ? (current?.mediaDuration ? current.mediaDuration * 1000 : 8000)
      : DEFAULT_DURATION_MS;

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i + 1 >= statuses.length) {
        onFinishedAll?.();
        return i;
      }
      return i + 1;
    });
  }, [statuses.length, onFinishedAll]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  /* The reply footer (StatusInput) contains several focusable elements —
     text field, attach button, send button. When focus hops between them,
     the browser fires blur on the old element before focus on the new one,
     so a naive onBlur={() => setPaused(false)} would let the story resume
     (and its auto-advance timer keep ticking) for a single frame every
     time the user tabs/taps between them. Checking, on the next frame,
     whether focus actually landed outside the footer avoids that — the
     story only resumes once the user has genuinely stepped away from the
     reply bar (e.g. tapped the content area to dismiss it). */
  const handleFooterBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (footerRef.current && !footerRef.current.contains(document.activeElement)) {
        setPaused(false);
      }
    });
  }, []);

  // Keep the story paused for the whole time a reply is in flight, so it
  // can't auto-advance/close out from under a send that's still pending.
  useEffect(() => {
    if (replySending) setPaused(true);
  }, [replySending]);

  // mark viewed + reset progress whenever the active status changes
  useEffect(() => {
    if (!current) return;
    setProgress(0);
    elapsedRef.current = 0;
    startRef.current = null;
    if (!isOwn) onView?.(current.statusId, ownerChatId);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!current || paused) return;
    if (current.statusType === "video") return; // driven by video timeupdate instead

    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts - elapsedRef.current;
      elapsedRef.current = ts - startRef.current;

      const pct = Math.min(100, (elapsedRef.current / duration) * 100);
      setProgress(pct);

      if (pct >= 100) {
        goNext();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [index, paused, duration, current, goNext]);

  // video-driven progress
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused, index]);

  if (!current) return null;

  const total    = statuses.length;
  const isVideo  = current.statusType === "video";
  const isText   = current.statusType === "text";
  const mediaSrc =
    current.decryptedMediaUrl ??
    current.mediaUrl ??
    current.media ??
    null;

  return (
  <div
  className={[
    mobileView
      ? "fixed inset-0"
      : "absolute inset-0",
    "z-[70] bg-black flex flex-col select-none",
  ].join(" ")}
>
      {/* progress segments */}
      <div className="flex gap-1 px-2 pt-2">
        {statuses.map((_, i) => (
          <div key={i} className="flex-1 h-[3px] rounded-full bg-white/30 overflow-hidden">
            <div
              className="h-full bg-white"
              style={{
                width: i < index ? "100%" : i === index ? `${progress}%` : "0%",
                transition: i === index ? "none" : "width 120ms linear",
              }}
            />
          </div>
        ))}
      </div>

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <Avatar src={ownerAvatar} name={ownerName} size={36} />
          <div className="leading-tight">
            <div className="text-white text-sm font-medium">{ownerName}</div>
            <div className="text-white/70 text-xs">{formatTimeAgo(current.createdAt)}</div>
          </div>
        </div>
        <button onClick={onClose} className="text-white p-1">
          <IconClose />
        </button>
      </div>

      {/* content */}
      <div
        className="relative flex-1 flex items-center justify-center overflow-hidden"
        onMouseDown={() => setPaused(true)}
        onMouseUp={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        {isText && (
          <div
            className="absolute inset-0 flex items-center justify-center px-8"
            style={{ background: current.backgroundColor || TEXT_BACKGROUNDS[0] }}
          >
            <p className="text-white text-2xl sm:text-3xl text-center font-medium leading-snug">
              {current.decryptedText ?? current.text}
            </p>
          </div>
        )}

        {!isText && isVideo && mediaSrc && (
          <video
            ref={videoRef}
            src={mediaSrc}
            className="max-h-full max-w-full"
            autoPlay
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onEnded={goNext}
          />
        )}

        {!isText && !isVideo && mediaSrc && (
          <img src={mediaSrc} alt="" className="max-h-full max-w-full object-contain" />
        )}

        {(current.decryptedCaption ?? current.caption) &&
         !isText && (
          <div className="absolute bottom-4 left-0 right-0 px-4 text-center">
            <span className="inline-block bg-black/40 text-white text-sm rounded-lg px-3 py-1.5">
              {current.decryptedCaption ??
               current.caption}
            </span>
          </div>
        )}

        {/* tap zones */}
        <button
          aria-label="Previous status"
          onClick={goPrev}
          className="absolute left-0 top-0 h-full w-1/3"
        />
        <button
          aria-label="Next status"
          onClick={goNext}
          className="absolute right-0 top-0 h-full w-2/3"
        />
      </div>

      {/* footer */}
      <div className="flex items-center justify-between px-4 py-3">
        {isOwn ? (
          <>
            <div className="flex items-center gap-1.5 text-white/80 text-sm">
              <IconEye />
              <span>{current.viewsCount ?? 0} viewed</span>
            </div>
            <button
              onClick={() => onDelete?.(current.statusId)}
              className="flex items-center gap-1.5 text-white/80 text-sm"
            >
              <IconTrash />
              <span>Delete</span>
            </button>
          </>

) : (
          <div
            ref={footerRef}
            className="flex-1"
            onFocus={() => setPaused(true)}
            onBlur={handleFooterBlur}
          >
            <StatusInput
              placeholder={`Reply to ${ownerName}…`}
              disabled={replySending}
              sending={replySending}
              onSendText={(trimmed) => onReply?.(current, trimmed)}
              onSendMedia={(file, meta) =>
                onReply?.(current, meta.isVoice ? "🎤 Voice message" : (meta.fileName || "Media"))
              }
            />
          </div>
        )}
      </div>

      <div className="text-center pb-1 text-white/40 text-[11px]">
        {index + 1} / {total}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN — Status list screen
─────────────────────────────────────────────────────────────────────────────── */
export function Status({

  socket,

  currentUser,

  users = [],

  onBack,

  mobileView,

  statusSocket,

  statusNotifications,

  setStatusNotifications,

  myStatusIds,

  myStatuses = [],

  recentGroups = [],

  viewedGroups = [],

  viewedIds = new Set(),

  onStatusViewed,

  onStatusChanged,

  /* ── DM-reply wiring ──
     These mirror what ChatWindow/useSendMessage already need to post a
     DM. getUserKey/uuid/showToast fall back to sane defaults so this
     still works even if a parent hasn't threaded them through yet. */
  setMessages,

  setUsers,

  getUserKey,

  uuid,

  showToast,

  setSelectedItem,
   openDmChat,

}) {

  const myChatId =
    currentUser?.chatInfo?.[0]?.chatId?.toString() || "";

  const myName =
    currentUser?.chatInfo?.[0]?.userName ||
    currentUser?.chatInfo?.[0]?.username ||
    currentUser?.username ||
    currentUser?.name ||
    "You";

  const myAvatar =
    currentUser?.chatInfo?.[0]?.profileImageUrl ||
    currentUser?.chatInfo?.[0]?.avatarUrl ||
    currentUser?.profileImageUrl ||
    null;

  /* Fallbacks so sendStatusMessage() always has what it needs, even if
     a parent layout hasn't been updated to pass every prop yet. */
  const resolveUserKey =
    getUserKey || ((chatId) => (chatId ? `user-${chatId}` : null));

  const genUuid = uuid || uuidv4;

  const notify = showToast || (() => {});

  /* Live "my statuses" derived from the shared statusNotifications state
     owned by GroupChat, instead of the (now unused) myStatuses prop.
     Sorted current → old so the story-rail preview and the viewer both
     lead with the most recent status. */
  const liveMyStatuses = sortStatusesNewestFirst(
    statusNotifications.filter(
      (status) =>
        status.ownerChatId === myChatId &&
        !status.isDeleted
    )
  );

  /* ===========================================
     STATUS SOCKET — shared instance from GroupChat
  =========================================== */

 const {

  createStatus,

  deleteStatus,

  viewStatus,

  resolveStatusKey,

  decryptStatusForViewing,

} = statusSocket;
  /* ===========================================
     VIEW / DELETE / CREATE — report up, don't fetch
     Status.js has no knowledge of IndexedDB or
     localStorage; the parent owns that and passes
     the resulting data back down as props.
  =========================================== */

  const handleView = useCallback(

    async (statusId, ownerChatId) => {

      await resolveStatusKey(
        statusId,
        ownerChatId
      );

      viewStatus?.(
        statusId
      );

      onStatusViewed?.(
        statusId
      );

    },

    [

      resolveStatusKey,

      viewStatus,

      onStatusViewed,

    ]

  );

  const [composerOpen, setComposerOpen] = useState(false);
  const [viewerGroup,  setViewerGroup]  = useState(null); // { ownerChatId, statuses, startIndex, isOwn }
  const [replySending, setReplySending] = useState(false);

  /* previewCache: statusId -> decrypted preview fields (decryptedText,
     decryptedMediaUrl, decryptedThumbnail, ...). This lets the story-rail
     tiles show the actual status content instead of just a color swatch,
     without waiting for the user to open the viewer first. */
  const [previewCache, setPreviewCache] = useState({});

  // Reacts to the shared statusNotifications state (updated by GroupChat's
  // statusSocket onViewsUpdated) instead of relying on a hook callback here.
  useEffect(() => {

    if (!viewerGroup) return;

    setViewerGroup((prev) => {

      if (!prev) return prev;

      return {

        ...prev,

        statuses: prev.statuses.map((status) => {

          const updated =
            statusNotifications.find(
              (s) =>
                s.statusId ===
                status.statusId
            );

          return updated
            ? {
                ...status,
                viewsCount:
                  updated.viewsCount ??
                  status.viewsCount,
              }
            : status;

        }),

      };

    });

  }, [
    statusNotifications,
    viewerGroup,
  ]);

  const handleDelete = useCallback(async (statusId) => {
    await deleteStatus?.(statusId);
    setViewerGroup(null);
    onStatusChanged?.();
  }, [deleteStatus, onStatusChanged]);

  const handleComposerSubmit = useCallback(
    (payload) => {
      createStatus?.({
        ownerChatId: myChatId,
        statusId: uuidv4(),
        ...payload,
      });
      setComposerOpen(false);
      onStatusChanged?.();
    },
    [createStatus, myChatId, onStatusChanged]
  );

  /* ===========================================
     REPLY TO A STATUS — sends an actual E2EE DM
     to the status owner via sendStatusMessage(),
     quoting the status the same way a normal DM
     reply quotes another message.
  =========================================== */
// Status.js — after
const handleReplyToStatus = useCallback(
  async (status, text, mediaFile, mediaMeta) => {
    if (!socket || !status?.statusId || !status?.ownerChatId) return;
    try {
      setReplySending(true);
      await sendStatusMessage({
        socket,
        myChatId,
        status,
        replyText: text,
        mediaFile,
        mediaMeta,
        uploadMedia,              // ← needs to be imported/passed into Status.js
        getUserKey: resolveUserKey,
        setMessages,
        setUsers,
        showToast: notify,
        uuid: genUuid,
      });

// Open the DM conversation immediately after the reply is sent.
// The parent (DesktopChatLayout/MobileAppLayout) already knows
// how to select the chat, switch to the Chats tab and emit
// "select-chat", so reuse that helper.
if (typeof openDmChat === "function") {

  const user =
    users.find(
      (u) =>
        String(u.chatId) ===
        String(status.ownerChatId)
    );

  openDmChat(
    status.ownerChatId,
    user?.userName ||
    user?.username ||
    user?.displayName
  );

} else {

  // Fallback for older parents that haven't been updated yet.
  setSelectedItem?.({
    type: "user",
    chatId: status.ownerChatId,
  });

}

setViewerGroup(null);

      } catch (err) {

        notify("Failed to send reply.", "error");

      } finally {

        setReplySending(false);

      }

    },
  [
  socket,
  myChatId,
  users,
  resolveUserKey,
  setMessages,
  setUsers,
  notify,
  genUuid,
  setSelectedItem,
  openDmChat,
]
  );

  const openMyStatuses = async () => {

    if (!liveMyStatuses.length) {

      setComposerOpen(true);

      return;

    }

    // liveMyStatuses is already sorted current → old, so decrypting in
    // place (Promise.all preserves input order) keeps the viewer's
    // playback order newest-first too.
    const decryptedStatuses =
      await Promise.all(

        liveMyStatuses.map(async (status) => {

          const decrypted =
            await decryptStatusForViewing(
              status
            );

          return {

            ...status,

            ...(decrypted || {}),

          };

        })

      );

    setViewerGroup({

      ownerChatId: myChatId,

      statuses: decryptedStatuses,

      startIndex: 0,

      isOwn: true,

    });

  };

  /* Live status groups (everyone but me), derived from the shared
     statusNotifications state, instead of the (now unused) recentGroups /
     viewedGroups props. Each group's statuses are sorted current → old so
     its preview tile and, if opened, the viewer both lead with the most
     recent status. Groups are then split into "recent" (has at least one
     unviewed status) vs "viewed" (every status in the group has already
     been viewed) buckets, mirroring WhatsApp's own split. */
  const groupedStatuses = Object.values(

    statusNotifications
      .filter(
        (status) =>
          status.ownerChatId !== myChatId &&
          !status.isDeleted
      )
      .reduce((acc, status) => {

        if (!acc[status.ownerChatId]) {

          const user = users.find(
            (u) => u.chatId === status.ownerChatId
          );

          acc[status.ownerChatId] = {

            ownerChatId: status.ownerChatId,

            name:
              user?.displayName ||
              user?.userName ||
              "Unknown",

            avatar:
              user?.profileImageUrl ??
              null,

            statuses: [],

          };

        }

        acc[status.ownerChatId].statuses.push(status);

        return acc;

      }, {})

  ).map((group) => {

    const statuses =
      sortStatusesNewestFirst(group.statuses);

    const fullyViewed =
      statuses.every((status) =>
        viewedIds.has(status.statusId)
      );

    return {

      ...group,

      statuses,

      fullyViewed,

    };

  });

  const liveRecentGroups =
    groupedStatuses.filter(
      (group) => !group.fullyViewed
    );

  const liveViewedGroups =
    groupedStatuses.filter(
      (group) => group.fullyViewed
    );

  const openGroup = async (group) => {

    // Sort defensively here too — `group` may come from liveRecentGroups /
    // liveViewedGroups (already sorted) or from an externally-supplied
    // group (order not guaranteed), so this keeps behavior consistent
    // either way.
    const sortedStatuses = sortStatusesNewestFirst(group.statuses);

    const firstUnviewedIdx =
      sortedStatuses.findIndex(

        (s) => !viewedIds.has(
          s.statusId
        )

      );

    const decryptedStatuses =
      await Promise.all(

        sortedStatuses.map(
          async (status) => {

            const decrypted =
              await decryptStatusForViewing(
                status
              );

            return {

              ...status,

              ...(decrypted || {}),

            };

          }

        )

      );

    setViewerGroup({

      ownerChatId:
        group.ownerChatId,

      name:
        group.name,

      avatar:
        group.avatar,

      statuses:
        decryptedStatuses,

      startIndex:
        firstUnviewedIdx === -1
          ? 0
          : firstUnviewedIdx,

      isOwn: false,

    });

  };

  /* Decrypt a preview (the most recent status) for every tile shown in the
     story rail — "My status", each recent group, and each viewed group —
     so the tile can render real content instead of a plain color swatch.
     Previously this content was only decrypted inside openMyStatuses /
     openGroup, i.e. only after the user tapped the tile. Groups are sorted
     current → old first so "most recent" reliably means index 0. */
  useEffect(() => {

    const groupsNeedingPreview = [
      ...(liveMyStatuses.length
        ? [{ statuses: liveMyStatuses }]
        : []),
      ...liveRecentGroups,
      ...liveViewedGroups,
    ];

    const toDecrypt = [];

    groupsNeedingPreview.forEach((g) => {
      const statuses = sortStatusesNewestFirst(g.statuses || []);
      const preview = statuses[0];
      if (!preview) return;

      const alreadyDecrypted =
        previewCache[preview.statusId] ||
        preview.decryptedText ||
        preview.decryptedMediaUrl ||
        preview.decryptedThumbnail;

      if (!alreadyDecrypted) {
        toDecrypt.push(preview);
      }
    });

    if (!toDecrypt.length) return;

    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        toDecrypt.map(async (status) => {
          const decrypted = await decryptStatusForViewing(status);
          return [status.statusId, decrypted || {}];
        })
      );

      if (cancelled) return;

      setPreviewCache((prev) => {
        const next = { ...prev };
        entries.forEach(([statusId, data]) => {
          next[statusId] = data;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };

  }, [
    liveMyStatuses,
    liveRecentGroups,
    liveViewedGroups,
    previewCache,
    decryptStatusForViewing,
  ]);

  /* Combined list purely for the empty-state check below the story rail */
  const hasAnyStatuses =
    liveMyStatuses.length > 0 ||
    liveRecentGroups.length > 0 ||
    liveViewedGroups.length > 0;

  return (
    /* Outer shell never scrolls — mirrors ChatList.js's "aside" container.
       Only the region marked overflow-y-auto below is allowed to scroll,
       so the floating compose button and story rail always stay put and
       are never pushed off-screen by the mobile browser's URL bar. */
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white dark:bg-neutral-950">
      {/* header */}
           <div className="shrink-0 flex items-center gap-2 px-2 py-3 border-b border-neutral-100 dark:border-neutral-800">
        {mobileView && onBack && (
          <button
            onClick={onBack}
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
            aria-label="Back"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
        )}
        <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          Status
        </h1>
      </div>

      {/* STORY RAIL — horizontally scrollable, vertically fixed */}
   <div
  className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800"
  style={{
    paddingTop: "1rem", // move header slightly down
  }}
>
        <div
  className="status-story-rail flex gap-3 overflow-x-auto overscroll-x-contain px-4 py-3"
  style={{
    marginTop: "0.5rem", // small space below header
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
  }}
>
          {liveMyStatuses.length > 0 ? (
            /* Have an active status → show it here instead of "Add status".
               Once it expires, liveMyStatuses empties out and this slot
               automatically falls back to the Add-status tile below. */
            <div className="relative shrink-0">
              {(() => {
                // liveMyStatuses is sorted current → old, so index 0 is
                // the most recent status — use it for the preview tile.
                const myPreviewStatus = liveMyStatuses[0];
                const myPreview =
                  (myPreviewStatus && previewCache[myPreviewStatus.statusId]) || {};

                return (
                  <StatusStoryCard
                    name="My status"
                    avatarSrc={myAvatar}
                    ringComplete={false}
                    segments={liveMyStatuses.length}
                    subtitle={`${liveMyStatuses.length} update${liveMyStatuses.length > 1 ? "s" : ""}`}
                    onClick={openMyStatuses}
                    statusType={myPreviewStatus?.statusType}
                    previewText={
                      myPreview.decryptedText ?? myPreviewStatus?.decryptedText
                    }
                    previewMediaUrl={
                      myPreview.decryptedMediaUrl ?? myPreviewStatus?.decryptedMediaUrl
                    }
                    previewThumbnail={
                      myPreview.decryptedThumbnail ?? myPreviewStatus?.decryptedThumbnail
                    }
                    previewBackgroundColor={myPreviewStatus?.backgroundColor}
                  />
                );
              })()}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setComposerOpen(true);
                }}
                className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366] text-white border-2 border-white dark:border-neutral-950 shadow touch-manipulation"
                aria-label="Add another status"
              >
                <IconPlus />
              </button>
            </div>
          ) : (
            <AddStatusCard
              avatarSrc={myAvatar}
              name={myName}
              onClick={() => setComposerOpen(true)}
            />
          )}

          {liveRecentGroups.map((g) => {
            // g.statuses is already sorted current → old (see
            // liveRecentGroups above), so index 0 is the most recent.
            const preview = g.statuses[0];
            const previewData = (preview && previewCache[preview.statusId]) || {};

            return (
              <StatusStoryCard
                key={`recent-${g.ownerChatId}`}
                name={g.name}
                avatarSrc={g.avatar}
                ringComplete={false}
                segments={g.statuses.length}
                subtitle={formatTimeAgo(preview?.createdAt)}
                onClick={() => openGroup(g)}
                statusType={preview?.statusType}
                previewText={previewData.decryptedText ?? preview?.decryptedText}
                previewMediaUrl={previewData.decryptedMediaUrl ?? preview?.decryptedMediaUrl}
                previewThumbnail={previewData.decryptedThumbnail ?? preview?.decryptedThumbnail}
                previewBackgroundColor={preview?.backgroundColor}
              />
            );
          })}

          {liveViewedGroups.map((g) => {
            // viewedGroups comes from outside Status.js, so sort defensively
            // here rather than assuming the parent already ordered it.
            const sorted = sortStatusesNewestFirst(g.statuses);
            const preview = sorted[0];
            const previewData = (preview && previewCache[preview.statusId]) || {};

            return (
              <StatusStoryCard
                key={`viewed-${g.ownerChatId}`}
                name={g.name}
                avatarSrc={g.avatar}
                ringComplete
                segments={sorted.length}
                subtitle={formatTimeAgo(preview?.createdAt)}
                onClick={() => openGroup({ ...g, statuses: sorted })}
                statusType={preview?.statusType}
                previewText={previewData.decryptedText ?? preview?.decryptedText}
                previewMediaUrl={previewData.decryptedMediaUrl ?? preview?.decryptedMediaUrl}
                previewThumbnail={previewData.decryptedThumbnail ?? preview?.decryptedThumbnail}
                previewBackgroundColor={preview?.backgroundColor}
              />
            );
          })}
        </div>
      </div>

      {/* SCROLLABLE BODY — reserved for future sections (e.g. channels);
          also shows the empty-state message. Padding-bottom keeps content
          clear of the floating compose button. */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-24">
        {!hasAnyStatuses && (
          <div className="flex flex-col items-center justify-center h-full text-center text-sm text-neutral-400 dark:text-neutral-500">
            No status updates yet. Statuses from people you follow will show up here.
          </div>
        )}
      </div>

      {/* floating composer button — sits in the non-scrolling outer shell,
          so it's never covered when the browser chrome shows/hides. */}
      <button
        onClick={() => setComposerOpen(true)}
        className="absolute right-6 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg active:scale-95 transition-transform"
        style={{
          bottom: mobileView
            ? "calc(6rem + env(safe-area-inset-bottom, 0px))"
            : "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
        }}
        aria-label="Add status"
      >
        <IconCamera />
      </button>

      {composerOpen && (
        <StatusComposer
  mobileView={mobileView}
  onClose={() => setComposerOpen(false)}
  onSubmit={handleComposerSubmit}
/>
      )}

      {viewerGroup && (
        <StatusViewer
          mobileView={mobileView}
          ownerChatId={viewerGroup.ownerChatId}
          ownerName={viewerGroup.isOwn ? "My status" : viewerGroup.name}
          ownerAvatar={viewerGroup.isOwn ? myAvatar : viewerGroup.avatar}
          statuses={viewerGroup.statuses}
          startIndex={viewerGroup.startIndex}
          isOwn={viewerGroup.isOwn}
          onClose={() => setViewerGroup(null)}
          onFinishedAll={() => setViewerGroup(null)}
          onView={handleView}
          onDelete={handleDelete}
          onReply={handleReplyToStatus}
          replySending={replySending}
        />
      )}

      {/* hide the horizontal scrollbar on the story rail (WebKit) */}
      <style>{`
        .status-story-rail::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

export default Status;